/**
 * The bearer token, and the one place that decides where it lives.
 *
 * `localStorage`, not a cookie: the backend issues a signed token in the
 * `Authorization` header (backend/app/api/auth.py), which means no browser ever attaches
 * a credential to a cross-origin request by itself and there is no CSRF surface to think
 * about. The trade is that the token is readable by any script running on the page — which
 * is a statement about XSS, and the answer to XSS is not to hide the token from ourselves
 * but to not have XSS. React escapes by default and nothing here renders raw HTML.
 *
 * `subscribe` exists because a 401 can arrive from any of a dozen background polls, and the
 * app has to react once, in one place, rather than have every hook learn about auth.
 */

const STORAGE_KEY = "kopateli.token";

type Listener = (token: string | null) => void;

const listeners = new Set<Listener>();
/** Mirror of the stored value, so a read costs nothing and works when storage is blocked. */
let current: string | null = read();

function read(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Safari in private mode, or storage disabled by policy. The session then lives for
    // exactly one page view, which is worse but still usable.
    return null;
  }
}

export const getToken = (): string | null => current;

export const hasToken = (): boolean => current !== null;

export function setToken(token: string | null): void {
  current = token;
  try {
    if (token === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // See read(): the in-memory mirror above is still correct for this page view.
  }
  for (const listener of listeners) listener(token);
}

export const clearToken = (): void => setToken(null);

/** Called on every token change, including the clear that a 401 triggers. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `Authorization` header, or nothing at all when there is no session. */
export const authHeaders = (): Record<string, string> =>
  current === null ? {} : { Authorization: `Bearer ${current}` };
